import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { ArrowLeft, Eye, MousePointerClick, Target, TrendingUp } from 'lucide-react'
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useLandingAnalytics } from '@/hooks/useLandingPages'

const COLORS = {
  views: '#4A7C9B',
  conversions: '#5A8A6C',
  cta: '#D4942A',
}

const PERIODS = [
  { value: '7d', label: '7 días' },
  { value: '30d', label: '30 días' },
  { value: '90d', label: '90 días' },
  { value: 'all', label: 'Todo' },
]

function Kpi({
  icon: Icon,
  label,
  value,
  suffix,
}: {
  icon: typeof Eye
  label: string
  value: number | string
  suffix?: string
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-4 p-5">
        <div className="rounded-lg bg-primary/10 p-3 text-primary">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-2xl font-bold">
            {value}
            {suffix}
          </div>
          <div className="text-sm text-muted-foreground">{label}</div>
        </div>
      </CardContent>
    </Card>
  )
}

export const LandingPageAnalyticsPage = ({ pageId }: { pageId: string }) => {
  const [period, setPeriod] = useState('30d')
  const { data, isLoading } = useLandingAnalytics(pageId, period)

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/landing-pages/$pageId" params={{ pageId }}>
              <ArrowLeft className="mr-1 h-4 w-4" />
              Editor
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{data?.title ?? 'Analítica'}</h1>
            {data && (
              <span className="font-mono text-xs text-muted-foreground">/l/{data.slug}</span>
            )}
          </div>
          {data && (
            <Badge variant={data.status === 'published' ? 'success' : 'warning'}>
              {data.status === 'published' ? 'Publicada' : data.status}
            </Badge>
          )}
        </div>
        <Tabs value={period} onValueChange={setPeriod}>
          <TabsList>
            {PERIODS.map((p) => (
              <TabsTrigger key={p.value} value={p.value}>
                {p.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {isLoading || !data ? (
        <p className="text-muted-foreground">Cargando analítica…</p>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi icon={Eye} label="Vistas (sesiones)" value={data.views} />
            <Kpi icon={MousePointerClick} label="Clics en CTA" value={data.cta_clicks} />
            <Kpi icon={Target} label="Conversiones" value={data.conversions} />
            <Kpi
              icon={TrendingUp}
              label="Tasa de conversión"
              value={data.conversion_rate}
              suffix="%"
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Evolución diaria</CardTitle>
            </CardHeader>
            <CardContent>
              {data.timeseries.length === 0 ? (
                <p className="py-12 text-center text-sm text-muted-foreground">
                  Aún no hay datos en este período.
                </p>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={data.timeseries}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="views"
                      name="Vistas"
                      stroke={COLORS.views}
                      fill={COLORS.views}
                      fillOpacity={0.12}
                      strokeWidth={2}
                    />
                    <Area
                      type="monotone"
                      dataKey="conversions"
                      name="Conversiones"
                      stroke={COLORS.conversions}
                      fill={COLORS.conversions}
                      fillOpacity={0.18}
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Por fuente (utm_source)</CardTitle>
              </CardHeader>
              <CardContent>
                {data.by_source.length === 0 ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">Sin datos.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={data.by_source}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="key" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend />
                      <Bar
                        dataKey="views"
                        name="Vistas"
                        fill={COLORS.views}
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar
                        dataKey="conversions"
                        name="Conversiones"
                        fill={COLORS.conversions}
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Por campaña (utm_campaign)</CardTitle>
              </CardHeader>
              <CardContent>
                {data.by_campaign.length === 0 ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">Sin datos.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="pb-2 pr-4 font-medium">Campaña</th>
                        <th className="pb-2 pr-4 font-medium">Vistas</th>
                        <th className="pb-2 pr-4 font-medium">Conv.</th>
                        <th className="pb-2 font-medium">Tasa</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {data.by_campaign.map((c) => (
                        <tr key={c.key}>
                          <td className="py-2 pr-4 font-medium">{c.key}</td>
                          <td className="py-2 pr-4">{c.views}</td>
                          <td className="py-2 pr-4">{c.conversions}</td>
                          <td className="py-2">{c.conversion_rate}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </div>

          {data.by_variant.length > 1 && (
            <Card>
              <CardHeader>
                <CardTitle>Comparación A/B</CardTitle>
              </CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-4 font-medium">Variante</th>
                      <th className="pb-2 pr-4 font-medium">Vistas</th>
                      <th className="pb-2 pr-4 font-medium">Clics CTA</th>
                      <th className="pb-2 pr-4 font-medium">CTR</th>
                      <th className="pb-2 pr-4 font-medium">Conv.</th>
                      <th className="pb-2 font-medium">Tasa conv.</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {data.by_variant.map((v) => (
                      <tr key={v.variant}>
                        <td className="py-2 pr-4 font-medium">
                          {v.variant.length <= 2
                            ? `Variante ${v.variant.toUpperCase()}`
                            : v.variant}
                        </td>
                        <td className="py-2 pr-4">{v.views}</td>
                        <td className="py-2 pr-4">{v.cta_clicks}</td>
                        <td className="py-2 pr-4">{v.ctr}%</td>
                        <td className="py-2 pr-4">{v.conversions}</td>
                        <td className="py-2">{v.conversion_rate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  )
}

export default LandingPageAnalyticsPage
